import React from 'react'
import { Link } from 'react-router-dom'
import SEO from '../SEO'
import ContactForm from '../ContactForm'
import iohkLogo from '../../assets/clientLogos/iohklogo.webp'
import { trackCtaClick, trackOutboundClick } from '../../utils/analytics'
import './../../styles/CaseStudy.css'

/**
 * Inline Midnight mark — a clean crescent moon over a circular field, drawn
 * in the Midnight palette (deep navy + cyan rim light). The actual brand mark
 * is hosted behind a CDN security check; this is a tasteful, generic
 * representation used for layout purposes.
 */
const MidnightMark = ({ size = 96 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 120 120"
    xmlns="http://www.w3.org/2000/svg"
    aria-label="Midnight"
    role="img"
  >
    <defs>
      <radialGradient id="mn-bg" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#0a1635" />
        <stop offset="100%" stopColor="#020716" />
      </radialGradient>
      <linearGradient id="mn-rim" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#7aa2ff" />
        <stop offset="100%" stopColor="#00d4ff" />
      </linearGradient>
      <filter id="mn-blur" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur stdDeviation="0.8" />
      </filter>
    </defs>
    <circle cx="60" cy="60" r="56" fill="url(#mn-bg)" stroke="url(#mn-rim)" strokeWidth="1.5" />
    {/* crescent */}
    <path
      d="M 78 32 a 32 32 0 1 0 0 56 a 24 24 0 1 1 0 -56 z"
      fill="url(#mn-rim)"
      filter="url(#mn-blur)"
    />
    {/* tiny stars */}
    <circle cx="28" cy="40" r="1.4" fill="#7aa2ff" />
    <circle cx="40" cy="78" r="1.0" fill="#00d4ff" />
    <circle cx="92" cy="86" r="1.2" fill="#7aa2ff" />
  </svg>
)

/* Hero visual — animated stylised "ledger" with cascading particles
   that subtly evokes a privacy-preserving distribution. */
const MidnightHeroVisual = () => (
  <svg viewBox="0 0 460 460" xmlns="http://www.w3.org/2000/svg" className="cs-hero__svg" aria-hidden="true">
    <defs>
      <linearGradient id="cs-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0066ff" />
        <stop offset="100%" stopColor="#00d4ff" />
      </linearGradient>
      <radialGradient id="cs-glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#00d4ff" stopOpacity="0.45" />
        <stop offset="100%" stopColor="#00d4ff" stopOpacity="0" />
      </radialGradient>
    </defs>

    <circle cx="230" cy="230" r="200" fill="url(#cs-glow)">
      <animate attributeName="r" values="200;215;200" dur="6s" repeatCount="indefinite" />
    </circle>

    {/* big crescent moon */}
    <g transform="translate(230 230)">
      <circle r="120" fill="rgba(7,11,30,0.92)" stroke="rgba(122,162,255,0.45)" strokeWidth="2" />
      <path d="M 50 -94 a 110 110 0 1 0 0 188 a 80 80 0 1 1 0 -188 z" fill="url(#cs-grad)" opacity="0.92" />
      {/* stars */}
      <circle cx="-72" cy="-58" r="2" fill="#7aa2ff">
        <animate attributeName="opacity" values="0.4;1;0.4" dur="2.4s" repeatCount="indefinite" />
      </circle>
      <circle cx="-90" cy="20" r="1.6" fill="#00d4ff">
        <animate attributeName="opacity" values="0.3;1;0.3" dur="3s" repeatCount="indefinite" />
      </circle>
      <circle cx="-40" cy="80" r="1.8" fill="#7aa2ff">
        <animate attributeName="opacity" values="0.5;1;0.5" dur="2.7s" repeatCount="indefinite" />
      </circle>
    </g>

    {/* token distribution rays */}
    <g stroke="rgba(0,212,255,0.35)" strokeDasharray="2 6" fill="none">
      <line x1="60" y1="60" x2="200" y2="200">
        <animate attributeName="opacity" values="0.2;0.7;0.2" dur="3s" repeatCount="indefinite" />
      </line>
      <line x1="400" y1="100" x2="270" y2="220">
        <animate attributeName="opacity" values="0.3;0.8;0.3" dur="2.5s" repeatCount="indefinite" />
      </line>
      <line x1="80" y1="380" x2="220" y2="260">
        <animate attributeName="opacity" values="0.2;0.6;0.2" dur="3.5s" repeatCount="indefinite" />
      </line>
      <line x1="380" y1="380" x2="260" y2="270">
        <animate attributeName="opacity" values="0.3;0.7;0.3" dur="2.8s" repeatCount="indefinite" />
      </line>
    </g>

    {/* small floating tokens */}
    <g>
      <circle cx="60" cy="60" r="6" fill="url(#cs-grad)">
        <animate attributeName="cy" values="60;52;60" dur="4s" repeatCount="indefinite" />
      </circle>
      <circle cx="400" cy="100" r="6" fill="url(#cs-grad)">
        <animate attributeName="cy" values="100;90;100" dur="3.6s" repeatCount="indefinite" />
      </circle>
      <circle cx="80" cy="380" r="5" fill="url(#cs-grad)">
        <animate attributeName="cy" values="380;370;380" dur="4.2s" repeatCount="indefinite" />
      </circle>
      <circle cx="380" cy="380" r="6" fill="url(#cs-grad)">
        <animate attributeName="cy" values="380;370;380" dur="3.4s" repeatCount="indefinite" />
      </circle>
    </g>
  </svg>
)

const Midnight = () => (
  <>
    <SEO
      path="/case-studies/midnight"
      title="Midnight (Cardano / IOHK) — QA, Compliance & ETL Case Study"
      description="QA Tech led and architected the QA, compliance and ETL programme for Midnight — Cardano's privacy-first sidechain by IOHK / IOG — from inception through to mainnet, supporting a token distribution of over 24 billion NIGHT tokens."
      keywords="Midnight Cardano case study, IOHK QA, IOG QA, Cardano sidechain testing, blockchain QA consultancy, NIGHT token distribution, ETL testing case study, blockchain compliance testing, mainnet launch QA"
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline:
          'Midnight (Cardano / IOHK) — QA, Compliance & ETL from inception to mainnet',
        author: { '@type': 'Organization', name: 'QA Tech' },
        publisher: {
          '@type': 'Organization',
          name: 'QA Tech',
          url: 'https://qatechltd.com',
        },
        about: ['Midnight', 'Cardano', 'IOHK', 'IOG', 'Blockchain QA'],
        url: 'https://qatechltd.com/case-studies/midnight',
      }}
    />

    <div className="body cs-body">
      {/* HERO ---------------------------------------------------- */}
      <header className="cs-hero">
        <div className="cs-hero__bg" aria-hidden="true">
          <span className="cs-hero__orb cs-hero__orb--a" />
          <span className="cs-hero__orb cs-hero__orb--b" />
          <span className="cs-hero__grid" />
        </div>

        <div className="cs-hero__inner">
          <nav className="sp-breadcrumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true">/</span>
            <Link to="/case-studies">Case Studies</Link>
            <span aria-hidden="true">/</span>
            <span>Midnight</span>
          </nav>

          <div className="cs-hero__content">
            <div className="cs-hero__client">
              <MidnightMark size={56} />
              <div className="cs-hero__client-text">
                <span className="eyebrow">Case study</span>
                <h2 className="cs-hero__client-name">Midnight</h2>
                <p className="cs-hero__client-sub">A Cardano sidechain by IOHK / IOG</p>
              </div>
              <img
                src={iohkLogo}
                alt="IOHK / IOG — Input Output Global"
                className="cs-hero__partner"
                loading="lazy"
              />
            </div>

            <h1 className="cs-hero__title">
              QA, compliance &amp; ETL <span className="cs-hero__title-accent">from inception to mainnet.</span>
            </h1>

            <p className="cs-hero__lede">
              We led and architected the entire quality, compliance and data
              programme for Midnight &mdash; the Cardano-aligned privacy-first
              sidechain built by IOHK / IOG &mdash; from greenfield design
              through the <strong>Glacier Drop</strong> distribution and into
              the federated mainnet, supporting the launch of the
              24&nbsp;billion-token NIGHT supply.
            </p>

            <div className="cs-hero__cta-row">
              <a
                href="#cs-contact"
                className="sp-cta-primary"
                onClick={() => trackCtaClick('Work with us', 'case_study_midnight_hero')}
              >
                Work with us
              </a>
              <a href="#cs-overview" className="sp-cta-secondary">Read the case study &darr;</a>
            </div>
          </div>

          <div className="cs-hero__visual" aria-hidden="true">
            <MidnightHeroVisual />
          </div>
        </div>
      </header>

      {/* AT-A-GLANCE STAT STRIP ---------------------------------- */}
      <section className="cs-stats" aria-label="Project at a glance" id="cs-overview">
        <div className="cs-stats__inner">
          <div className="cs-stat">
            <span className="cs-stat__num">16 months</span>
            <span className="cs-stat__label">engagement (3 Sept 2024 &ndash; Jan 2026)</span>
          </div>
          <div className="cs-stat">
            <span className="cs-stat__num">24B</span>
            <span className="cs-stat__label">NIGHT total supply launched</span>
          </div>
          <div className="cs-stat">
            <span className="cs-stat__num">8 chains</span>
            <span className="cs-stat__label">Glacier Drop reach (BTC, ETH, ADA, SOL, BNB, XRP, AVAX, BAT)</span>
          </div>
          <div className="cs-stat">
            <span className="cs-stat__num">0 → mainnet</span>
            <span className="cs-stat__label">QA from greenfield to live</span>
          </div>
          <div className="cs-stat"><span className="cs-stat__num">Architected</span><span className="cs-stat__label">QA, compliance &amp; ETL programme</span></div>
        </div>
      </section>

      {/* MAIN CONTENT -------------------------------------------- */}
      <main className="cs-main">
        {/* CONTEXT */}
        <section className="sp-split sp-split--left">
          <div className="sp-split__inner">
            <div className="sp-split__heading">
              <span className="sp-split__num" aria-hidden="true">01</span>
              <span className="sp-split__kicker">The brief</span>
              <h2 className="sp-split__title">A privacy-first chain, built on the world stage.</h2>
            </div>
            <div className="sp-split__body">
              <p>
                Midnight is the Cardano-aligned, privacy-first sidechain
                engineered by IOHK / IOG. As a brand-new public chain, every
                line of code, every contract, every distribution event would be
                examined publicly &mdash; and would handle real value at scale.
              </p>
              <p>
                We were brought in to design and run the quality, compliance
                and data programme that would let the team move fast without
                compromising the trust the chain&rsquo;s name was being built
                on.
              </p>
            </div>
          </div>
          <span className="sp-split__rule" aria-hidden="true" />
        </section>

        {/* TEST STRATEGY */}
        <section className="sp-split sp-split--right">
          <div className="sp-split__inner">
            <div className="sp-split__heading">
              <span className="sp-split__num" aria-hidden="true">02</span>
              <span className="sp-split__kicker">QA architecture</span>
              <h2 className="sp-split__title">A test strategy designed before the first commit.</h2>
            </div>
            <div className="sp-split__body">
              <p>
                We led the QA architecture from inception &mdash; risk model,
                quality gates, test pyramid, environments, data strategy and
                ownership. By the time the team started building, every layer
                of the system already had a defined definition of done and a
                test owner.
              </p>
              <ul>
                <li>End-to-end test architecture &amp; quality gates</li>
                <li>Test case management &amp; coverage governance</li>
                <li>Risk-driven prioritisation aligned to launch milestones</li>
                <li>Embedded across engineering, data and compliance teams</li>
              </ul>
            </div>
          </div>
          <span className="sp-split__rule" aria-hidden="true" />
        </section>

        {/* AUTOMATION */}
        <section className="sp-split sp-split--left">
          <div className="sp-split__inner">
            <div className="sp-split__heading">
              <span className="sp-split__num" aria-hidden="true">03</span>
              <span className="sp-split__kicker">Automation</span>
              <h2 className="sp-split__title">Test automation across protocol, data and UI.</h2>
            </div>
            <div className="sp-split__body">
              <p>
                We built and maintained automated test suites covering protocol
                interactions, on-chain data flows, transaction lifecycles and
                user-facing surfaces. Pipelines were wired up in{' '}
                <strong>GitHub Actions</strong> as a first-class part of the
                development and delivery process &mdash; every change ran the
                relevant suites, and green or red was a clear, fast signal
                instead of a vibe.
              </p>
              <ul>
                <li>API and contract-level automation</li>
                <li>End-to-end transaction and lifecycle automation</li>
                <li>Regression suites that grew with the protocol</li>
                <li>GitHub Actions pipelines feeding the development &amp; delivery flow</li>
                <li>Stable, reviewable test code &mdash; treated like product code</li>
              </ul>
            </div>
          </div>
          <span className="sp-split__rule" aria-hidden="true" />
        </section>

        {/* MANUAL & EXPLORATORY */}
        <section className="sp-split sp-split--right">
          <div className="sp-split__inner">
            <div className="sp-split__heading">
              <span className="sp-split__num" aria-hidden="true">04</span>
              <span className="sp-split__kicker">Manual &amp; exploratory</span>
              <h2 className="sp-split__title">Where automation ends, human curiosity takes over.</h2>
            </div>
            <div className="sp-split__body">
              <p>
                Some risks are too subtle for scripts. We ran structured
                exploratory and manual test sessions across new flows,
                edge-case wallets, network conditions and adversarial scenarios
                &mdash; and fed every finding back into the automation backlog
                so it stayed caught after the first time.
              </p>
            </div>
          </div>
          <span className="sp-split__rule" aria-hidden="true" />
        </section>

        {/* ETL & DATA */}
        <section className="sp-split sp-split--left">
          <div className="sp-split__inner">
            <div className="sp-split__heading">
              <span className="sp-split__num" aria-hidden="true">05</span>
              <span className="sp-split__kicker">ETL &amp; data testing</span>
              <h2 className="sp-split__title">Reconciling the Glacier Drop, line by line.</h2>
            </div>
            <div className="sp-split__body">
              <p>
                The Glacier Drop targeted holders across eight chains
                &mdash; Bitcoin, Ethereum, Cardano, Solana, BNB Chain, XRP
                Ledger, Avalanche and BAT &mdash; based on a snapshot of
                qualifying wallets, with allocations unlocking on a 360-day
                thawing schedule. We architected the data testing programme
                behind it: ingesting source records, running ETL transforms,
                reconciling allocations against eligibility rules and
                verifying the on-chain output.
              </p>
              <ul>
                <li>End-to-end ETL validation across source, staging and chain</li>
                <li>Allocation &amp; eligibility reconciliation against rule sets</li>
                <li>Defect triage on the data layer with full audit trails</li>
                <li>Sign-off evidence for every batch released</li>
              </ul>
            </div>
          </div>
          <span className="sp-split__rule" aria-hidden="true" />
        </section>

        {/* COMPLIANCE & AUDIT */}
        <section className="sp-split sp-split--right">
          <div className="sp-split__inner">
            <div className="sp-split__heading">
              <span className="sp-split__num" aria-hidden="true">06</span>
              <span className="sp-split__kicker">Compliance &amp; auditing</span>
              <h2 className="sp-split__title">Defensible by design, auditable by default.</h2>
            </div>
            <div className="sp-split__body">
              <p>
                Compliance wasn&rsquo;t a phase at the end &mdash; it was
                built into the programme. Every test artefact, distribution
                batch and exception had a clear owner, a documented decision
                trail and an evidence package ready for audit. When external
                eyes arrived, the answer was always &ldquo;here it is&rdquo;,
                not &ldquo;give us a week&rdquo;.
              </p>
              <ul>
                <li>Compliance-led test design</li>
                <li>Audit trails on every artefact and decision</li>
                <li>Sign-off processes aligned to launch governance</li>
              </ul>
            </div>
          </div>
          <span className="sp-split__rule" aria-hidden="true" />
        </section>

        {/* CUSTOM TOOLING */}
        <section className="sp-split sp-split--left">
          <div className="sp-split__inner">
            <div className="sp-split__heading">
              <span className="sp-split__num" aria-hidden="true">07</span>
              <span className="sp-split__kicker">Custom tooling</span>
              <h2 className="sp-split__title">Python where the off-the-shelf tools fell short.</h2>
              <div className="sp-split__accent">
                <span className="sp-accent-code">{'$ python verify_allocations.py'}</span>
              </div>
            </div>
            <div className="sp-split__body">
              <p>
                The scale and shape of the data didn&rsquo;t fit any
                off-the-shelf tool. So we wrote our own &mdash; a suite of
                custom Python utilities to ingest, transform, reconcile and
                spot-check enormous datasets, with reproducible output and
                clean reports for engineering, compliance and leadership.
              </p>
              <ul>
                <li>Bulk reconciliation across millions of records</li>
                <li>Automated diffing between source, staging and chain</li>
                <li>Reusable test fixtures &amp; data generators</li>
                <li>Dashboards and CSV/JSON evidence outputs</li>
              </ul>
            </div>
          </div>
          <span className="sp-split__rule" aria-hidden="true" />
        </section>

        {/* DEV WORK */}
        <section className="sp-split sp-split--right">
          <div className="sp-split__inner">
            <div className="sp-split__heading">
              <span className="sp-split__num" aria-hidden="true">08</span>
              <span className="sp-split__kicker">Engineering contribution</span>
              <h2 className="sp-split__title">Not just testing — building too.</h2>
            </div>
            <div className="sp-split__body">
              <p>
                We rolled up our sleeves on the build as well. Where the
                programme needed code &mdash; helper services, integrations,
                tooling, test infrastructure &mdash; we wrote it, reviewed it,
                shipped it. Same standard as the team&rsquo;s production code,
                because that&rsquo;s exactly what it became.
              </p>
            </div>
          </div>
          <span className="sp-split__rule" aria-hidden="true" />
        </section>

        {/* UX/UI PARTNERSHIP */}
        <section className="sp-split sp-split--left">
          <div className="sp-split__inner">
            <div className="sp-split__heading">
              <span className="sp-split__num" aria-hidden="true">09</span>
              <span className="sp-split__kicker">Design partnership</span>
              <h2 className="sp-split__title">In the room with UX/UI from day one.</h2>
            </div>
            <div className="sp-split__body">
              <p>
                We worked side-by-side with the UX/UI designers building the
                user-facing app &mdash; helping shape requirements before
                they reached engineering, instead of validating them after
                the fact. Two questions ran through every design review:
                <em> is it accessible</em>, and <em>is it testable</em>?
              </p>
              <ul>
                <li>Co-authored requirements for user-facing flows</li>
                <li>Accessibility built in &mdash; semantic structure, keyboard paths, screen-reader labels, contrast</li>
                <li>Testability built in &mdash; stable selectors, predictable states, well-defined empty / loading / error views</li>
                <li>Acceptance criteria that designers, devs and QA could all sign off on</li>
              </ul>
              <p>
                The result: a user-facing experience that was inclusive by
                design and testable by default &mdash; instead of the usual
                last-minute scramble to retrofit either.
              </p>
            </div>
          </div>
          <span className="sp-split__rule" aria-hidden="true" />
        </section>

        {/* OUTCOME */}
        <section className="sp-split sp-split--right">
          <div className="sp-split__inner">
            <div className="sp-split__heading">
              <span className="sp-split__num" aria-hidden="true">10</span>
              <span className="sp-split__kicker">Outcome</span>
              <h2 className="sp-split__title">Glacier Drop live, NIGHT trading, mainnet shipped.</h2>
            </div>
            <div className="sp-split__body">
              <p>
                The Glacier Drop redemption portal opened publicly in August
                2025, with NIGHT trading on Cardano going live on{' '}
                <strong>December 8, 2025</strong>. The federated mainnet
                followed in Q1 2026, with IOG and major corporate partners
                running nodes. The 24&nbsp;billion NIGHT supply was minted in
                anticipation of distribution, with billions claimed across the
                Glacier Drop and Scavenger Mine phases.
              </p>
              <p>
                It was an event that would have been forensically picked apart
                if anything had gone wrong &mdash; and didn&rsquo;t. The QA,
                compliance and data programmes we architected and led held up
                under public scrutiny on one of the most-watched launches in
                the Cardano ecosystem.
              </p>
            </div>
          </div>
          <span className="sp-split__rule" aria-hidden="true" />
        </section>

        {/* PRESS & COVERAGE */}
        <section className="cs-press" aria-label="Press and coverage">
          <div className="cs-press__inner">
            <span className="sp-stack__label">Press &amp; coverage</span>
            <p className="cs-press__lead">
              Selected reporting and official announcements from the Midnight
              launch programme.
            </p>
            <ul className="cs-press__list">
              {[
                {
                  href: 'https://midnight.network/blog/guide-to-the-night-token-launch-and-redemption',
                  src: 'Midnight (official blog)',
                  title: 'Guide to the NIGHT token launch and Redemption',
                },
                {
                  href: 'https://www.midnight.gd/news/night-redemption-portal-now-live',
                  src: 'Midnight TGE',
                  title: 'NIGHT Redemption portal is now live',
                },
                {
                  href: 'https://www.coindesk.com/markets/2025/08/06/cardano-s-midnight-begins-night-airdrop-across-eight-chains-in-privacy-powered-rollout',
                  src: 'CoinDesk',
                  title:
                    "Cardano's Midnight Begins NIGHT Airdrop Across Eight Chains in Privacy-Powered Rollout",
                },
                {
                  href: 'https://midnight.network/blog/state-of-the-network-december-2025',
                  src: 'Midnight (official blog)',
                  title: 'State of the Network — December 2025',
                },
                {
                  href: 'https://thecryptobasic.com/2025/08/05/cardano-opens-midnight-airdrop-claim-portal-for-xrp-and-ada-users/',
                  src: 'The Crypto Basic',
                  title:
                    'Midnight Glacier Drop Claim Portal Now Open for XRP and Cardano Users',
                },
                {
                  href: 'https://beincrypto.com/midnight-network-fair-token-distribution/',
                  src: 'BeInCrypto',
                  title:
                    'Midnight Network Tokenomics — a Radically Accessible and Fair Token Distribution Model',
                },
                {
                  href: 'https://midnight.network/night',
                  src: 'Midnight (official)',
                  title: 'NIGHT token — Midnight network',
                },
              ].map((p) => (
                <li key={p.href}>
                  <a
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      trackOutboundClick(p.href, p.title, 'case_study_midnight_press')
                    }
                  >
                    <span className="cs-press__src">{p.src}</span>
                    <span className="cs-press__title">{p.title}</span>
                  </a>
                </li>
              ))}
            </ul>
            <p className="cs-press__note">
              External links open in a new tab. Coverage attributed to its
              respective publishers; QA Tech is independent and not affiliated
              with Midnight, IOHK / IOG or Cardano.
            </p>
          </div>
        </section>

        {/* WHAT WE DELIVERED -------------------------------------- */}
        <section className="sp-stack" aria-label="Disciplines we delivered">
          <div className="sp-stack__inner">
            <h3 className="sp-stack__label">What we delivered</h3>
            <ul className="sp-stack__list">
              {[
                'Test strategy',
                'Test architecture',
                'Test case management',
                'Test automation',
                'Manual & exploratory testing',
                'Regression testing',
                'System & end-to-end testing',
                'ETL & data testing',
                'Compliance & auditing',
                'Custom tooling & development',
                'Python scripting at scale',
                'UX/UI partnership',
                'Accessibility & testability requirements',
                'CI/CD pipelines (GitHub Actions)',
              ].map((t) => (
                <li key={t} className="sp-stack__item">{t}</li>
              ))}
            </ul>
          </div>
        </section>

        {/* TECH & TOOLS ------------------------------------------- */}
        <section className="sp-stack" aria-label="Technologies we used">
          <div className="sp-stack__inner">
            <h3 className="sp-stack__label">Tech &amp; tools</h3>
            <ul className="sp-stack__list">
              {[
                'Python',
                'Pytest',
                'Pandas',
                'TypeScript',
                'JavaScript',
                'Playwright',
                'Cypress',
                'PostgreSQL',
                'GitHub Actions',
                'Allure',
                'Grafana',
                'Cardano',
                'IOHK / IOG tooling',
              ].map((t) => (
                <li key={t} className="sp-stack__item">{t}</li>
              ))}
            </ul>
          </div>
        </section>
      </main>

      {/* CTA ----------------------------------------------------- */}
      <section className="sp-cta-section">
        <div className="sp-cta-section__inner">
          <h2>Got a high-stakes launch coming?</h2>
          <p>
            If you&rsquo;re shipping something the world will look at &mdash;
            on-chain, off-chain or anywhere in between &mdash; we&rsquo;d love
            to talk.
          </p>
        </div>
      </section>

      <div id="cs-contact">
        <ContactForm />
      </div>
    </div>
  </>
)

export default Midnight
